<template>
    <div>
        <div class="stl-table-container">
            <table class="stl-table stl-table-sortable">
                <thead>
                    <tr>
                        <th class="sortable" @click="setSort('roomName')">호 {{ sortMark("roomName") }}</th>
                        <th class="num sortable" @click="setSort('exclusiveArea')">전용면적(㎡) {{ sortMark("exclusiveArea") }}</th>
                        <th class="num sortable" @click="setSort('usageQty')">사용량({{ unitLabel(usageUnit) }}) {{ sortMark("usageQty") }}</th>
                        <th class="num sortable" @click="setSort('individualCost')">{{ L.usageShare }} {{ sortMark("individualCost") }}</th>
                        <th v-if="showAreaCol" class="num sortable" @click="setSort('allocatedCommonCost')">{{ L.areaShare }} {{ sortMark("allocatedCommonCost") }}</th>
                        <th class="num sortable" @click="setSort('manualAdjustAmount')">조정 {{ sortMark("manualAdjustAmount") }}</th>
                        <th class="num sortable" @click="setSort('totalAmount')">합계 {{ sortMark("totalAmount") }}</th>
                        <th v-if="adjustable" style="width: 60px;"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="d in sortedDetails" :key="d.sysNodeIdx">
                        <td class="ho">{{ d.roomName }}</td>
                        <td class="num">{{ fmtArea(d.exclusiveArea) }}</td>
                        <td class="num">{{ fmtUsage(d.usageQty) }}</td>
                        <td class="num">{{ fmtWon(d.individualCost) }}</td>
                        <td v-if="showAreaCol" class="num">{{ fmtWon(d.allocatedCommonCost) }}</td>
                        <td class="num" :title="d.adjustReason || ''">
                            {{ fmtWon(d.manualAdjustAmount) }}
                            <i v-if="d.adjustReason" class="fa fa-commenting-o" style="color: #94a3b8; margin-left: 2px;"></i>
                        </td>
                        <td class="num"><strong>{{ fmtWon(d.totalAmount) }}</strong></td>
                        <td v-if="adjustable">
                            <button type="button" class="stl-btn stl-btn-sm" style="height: 24px; padding: 0 8px; font-size: 11px;" @click="$emit('adjust', d)">
                                조정
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!sortedDetails.length">
                        <td :colspan="colCount">
                            <div class="stl-empty"><p>세대별 결과가 없습니다.</p></div>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="sortedDetails.length">
                    <tr>
                        <td>{{ householdCountFooterLabel(sortedDetails.length) }}</td>
                        <td class="num">{{ fmtArea(sum("exclusiveArea")) }}</td>
                        <td class="num">{{ fmtUsage(sum("usageQty")) }}</td>
                        <td class="num">{{ fmtWon(sum("individualCost")) }}</td>
                        <td v-if="showAreaCol" class="num">{{ fmtWon(sum("allocatedCommonCost")) }}</td>
                        <td class="num">{{ fmtWon(sum("manualAdjustAmount")) }}</td>
                        <td class="num">{{ fmtWon(sum("totalAmount")) }}</td>
                        <td v-if="adjustable"></td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <div v-if="sortedDetails.length && kepcoTotalAmount !== null" class="stl-row" style="padding: 8px 14px; justify-content: flex-end; border-top: 1px solid #e6e9ef;">
            <span class="stl-hint" :title="kDiffTooltip">
                {{ L.householdBillingTotal }} − {{ L.totalAmount }} =
                <strong class="num" :style="{ color: kDiff === 0 ? '#1e7e52' : '#a06d1a' }">{{ fmtWon(kDiff) }}원</strong>
                <span v-if="kDiff !== 0">(절삭 잔차 또는 조정 반영)</span>
            </span>
        </div>
    </div>
</template>

<script>
import { fmtWon, fmtUsage, fmtArea, unitLabel, SETTLEMENT_LABELS, householdCountFooterLabel, remainderDiffTooltip } from "../utils/settlementFormat.js";

export default {
    name: "HouseholdResultGrid",
    props: {
        details: { type: Array, default: () => [] },
        // DRAFT 상태에서만 true — 조정 버튼 노출
        adjustable: { type: Boolean, default: false },
        kepcoTotalAmount: { type: [Number, String], default: null },
        showAreaColumn: { type: Boolean, default: null },
    },
    data() {
        return {
            sortKey: "roomName",
            sortDir: 1,
        };
    },
    computed: {
        sortedDetails() {
            const rows = [...this.details];
            const key = this.sortKey;
            rows.sort((a, b) => {
                const av = a[key];
                const bv = b[key];
                if (typeof av === "string") return this.sortDir * String(av).localeCompare(String(bv), "ko");
                return this.sortDir * (Number(av || 0) - Number(bv || 0));
            });
            return rows;
        },
        kDiff() {
            return this.sum("totalAmount") - Math.trunc(Number(this.kepcoTotalAmount || 0));
        },
        kDiffTooltip() {
            return remainderDiffTooltip(this.sum("totalAmount"), this.kepcoTotalAmount, this.kDiff);
        },
        L() {
            return SETTLEMENT_LABELS;
        },
        usageUnit() {
            return this.details[0]?.usageUnit || "kWh";
        },
        showAreaCol() {
            if (this.showAreaColumn != null) return this.showAreaColumn;
            return this.details.some((d) => Number(d.allocatedCommonCost || 0) !== 0);
        },
        colCount() {
            return (this.showAreaCol ? 7 : 6) + (this.adjustable ? 1 : 0);
        },
    },
    methods: {
        fmtWon,
        fmtUsage,
        fmtArea,
        unitLabel,
        householdCountFooterLabel,
        sum(field) {
            return this.sortedDetails.reduce((s, d) => s + Number(d[field] || 0), 0);
        },
        setSort(key) {
            if (this.sortKey === key) this.sortDir *= -1;
            else {
                this.sortKey = key;
                this.sortDir = key === "roomName" ? 1 : -1;
            }
        },
        sortMark(key) {
            if (this.sortKey !== key) return "";
            return this.sortDir > 0 ? "▲" : "▼";
        },
    },
};
</script>
